// VAT Returns: page text and the list of countries. Generated file.
const config = {
  "slug": "vat-returns",
  "name": "VAT Returns",
  "menu": {
    "show": false,
    "label": "VAT Returns",
    "desc": "Prepared from your ERP data, country by country"
  },
  "meta": {
    "title": "VAT Returns Across Countries, From Your ERP | Taxera",
    "description": "VAT returns in every country, prepared from your ERP data and reconciled to your ledgers. A plain-English guide to VAT reporting, with tracked deadlines."
  },
  "hero": {
    "h1": "VAT returns in every country, prepared from your own ERP data.",
    "h1Accent": "Reconciled before they go.",
    "text": "Each country has its own return, its own deadlines and its own online portal. Taxera builds the figures straight from your live ERP data and ties them back to your ledgers, so your team stops stitching returns together in spreadsheets.",
    "secondaryCta": "See requirements by country"
  },
  "countriesSection": {
    "heading": "VAT return requirements by country",
    "intro": "Every country runs its VAT returns a little differently. Pick yours to see who has to file, the key dates and how the process works.",
    "trackerPath": "/tracker/vat_returns"
  },
  "what": {
    "heading": "What is a VAT return, in plain words?",
    "paragraphs": [
      "A VAT return is the periodic report a business sends to the tax authority. It adds up the VAT charged on sales and the VAT paid on purchases for a period, and shows how much is owed or can be reclaimed.",
      "For a company active in several countries, that means many returns with different periods, boxes, rounding rules, deadlines and submission portals. Each one has to agree with the books, and increasingly with the e-invoices and audit files the authority already receives."
    ],
    "explainer": {
      "heading": "Three things every VAT return depends on",
      "items": [
        {
          "icon": "Database",
          "title": "The right numbers",
          "text": "Every box on the return must trace back to real transactions in your ERP. If you cannot show where a figure came from, you cannot defend it."
        },
        {
          "icon": "FileCheck2",
          "title": "The right format and route",
          "text": "Each country wants the return in its own layout and submitted through its own portal or interface, often with its own checks."
        },
        {
          "icon": "Clock",
          "title": "The right time",
          "text": "Periods and deadlines differ by country and by company size, and they change. A missed date costs money even when the figures are right."
        }
      ]
    }
  },
  "steps": {
    "heading": "How Taxera does it",
    "intro": "From your ERP to a return that is ready for the authority, in four steps you do not have to run by hand.",
    "items": [
      {
        "icon": "PlugZap",
        "title": "Connect to your ERP",
        "text": "Taxera sits inside your ERP landscape (SAP, Oracle, Microsoft Dynamics and others). No file exports, no manual uploads."
      },
      {
        "icon": "Calculator",
        "title": "Prepare the return",
        "text": "The figures for each country are built from your transactions, using that country's boxes, periods and rules."
      },
      {
        "icon": "Scale",
        "title": "Check and reconcile",
        "text": "The return is matched to your ledgers and to your invoice data, and differences are flagged before anyone signs off."
      },
      {
        "icon": "CheckCircle2",
        "title": "Review and approve",
        "text": "Your team reviews the figures, with drill-down to the source transactions. The return is then ready for the route your country uses."
      }
    ]
  },
  "benefits": {
    "heading": "What changes for your team",
    "items": [
      {
        "icon": "FileSpreadsheet",
        "title": "No spreadsheets in the middle",
        "text": "Return figures come from your ERP without anyone re-keying or patching numbers."
      },
      {
        "icon": "Layers",
        "title": "One integration for everything",
        "text": "VAT returns use the same data as your e-Invoicing and SAF-T files, so the numbers agree across all of them."
      },
      {
        "icon": "RefreshCw",
        "title": "Reconciled by design",
        "text": "Returns are tied back to the ledgers and to filed invoice data, which is exactly what authorities now compare on their side."
      },
      {
        "icon": "BellRing",
        "title": "Rule changes are our problem",
        "text": "When a country changes a box, a deadline or a portal, we track it and update the platform. You do not have to rebuild anything."
      }
    ]
  },
  "faq": {
    "heading": "VAT returns, questions answered",
    "items": [
      {
        "q": "What is a VAT return?",
        "a": "A VAT return is the periodic report a business files with the tax authority. It shows the VAT charged on sales, the VAT paid on purchases and the balance to pay or reclaim for the period."
      },
      {
        "q": "How often do companies have to file VAT returns?",
        "a": "It depends on the country and on the size of the business. Monthly and quarterly returns are the most common, and some countries add annual returns or extra reports. Deadlines and thresholds change, so check the official rules for each country."
      },
      {
        "q": "Why are VAT returns harder for multinational companies?",
        "a": "Every country has its own return layout, periods, rounding rules and submission route, and the figures must agree with the ledgers, invoices and audit files. With many countries, the manual work and the risk of mismatches grow quickly."
      },
      {
        "q": "How do VAT returns relate to EC Sales Lists and Intrastat?",
        "a": "They report different parts of the same cross-border activity. EC Sales Lists report supplies to businesses in other EU countries, and Intrastat reports the movement of goods. Both should agree with the figures on the VAT return."
      },
      {
        "q": "How does Taxera prepare VAT returns from SAP or another ERP?",
        "a": "Taxera's middleware connects to your ERP, builds the figures for each country from your transactions, reconciles them to your ledgers and gives your team the return to review and approve, with drill-down to the source data."
      },
      {
        "q": "Do we need a separate tool for every country?",
        "a": "No. Taxera is one platform with one ERP integration. Country rules and layouts are handled inside the platform, alongside your e-Invoicing and SAF-T reporting."
      },
      {
        "q": "What about VAT groups?",
        "a": "In some countries, related companies can join a VAT group and file one return together. Taxera handles both: standard VAT returns for entities outside a group, and consolidated group figures for entities inside one. See the VAT Groups page for the countries and rules."
      }
    ]
  },
  "related": [
    {
      "to": "/tracker/vat_returns",
      "label": "Live VAT return changes",
      "note": "Free Global Tax Tracker"
    },
    {
      "to": "/solutions/vat-groups",
      "label": "VAT Groups",
      "note": "One return for several legal entities"
    },
    {
      "to": "/solutions/intrastat",
      "label": "Intrastat",
      "note": "Goods movements report"
    },
    {
      "to": "/solutions/tax-finance-operations",
      "label": "Tax & Finance Operations",
      "note": "Month-end reconciliations"
    },
    {
      "to": "/solutions/ec-sales-list",
      "label": "EC Sales List",
      "note": "Cross-border sales report"
    },
    {
      "to": "/solutions/e-invoicing",
      "label": "e-Invoicing",
      "note": "Same data, same integration"
    },
    {
      "to": "/solutions/saf-t",
      "label": "SAF-T reporting",
      "note": "Audit files from the same source"
    },
    {
      "to": "/blog",
      "label": "Latest articles and updates",
      "note": "Regulatory analysis from our team"
    }
  ],
  "cta": {
    "heading": "See VAT returns running on your own ERP data",
    "text": "Book a short demo. We will show you the path from your ledgers to a return that is ready to go."
  },
  "country": {
    "eyebrow": "VAT returns {name}",
    "headingShort": "VAT returns in {name}: {short} explained",
    "headingPlain": "VAT returns in {name}, explained",
    "mandateLabel": "The obligation",
    "whoLabel": "Who must file",
    "howLabel": "How it works",
    "datesHeading": "Key dates for {name}",
    "leftHeading": "What the return contains",
    "rightHeading": "What your ERP has to deliver",
    "flowHeading": "The process, step by step",
    "linkedHeading": "Linked obligations in {name}",
    "faqHeading": "VAT returns {name}: questions, answered",
    "moreHeading": "More VAT return guides",
    "allLabel": "All VAT return countries",
    "otherLabel": "VAT returns {name}",
    "downloadTitle": "Keep this guide",
    "downloadText": "Download the {name} VAT returns guide as a PDF. It has the same content as this page.",
    "printTitle": "VAT returns guide: {name}",
    "trackerPath": "/tracker/vat_returns",
    "faqs": [
      {
        "q": "What is the VAT return in {name}?",
        "a": [
          "mandateName",
          "summary"
        ]
      },
      {
        "q": "Who has to file a VAT return in {name}?",
        "a": [
          "inScope"
        ]
      },
      {
        "q": "What are the key dates for VAT returns in {name}?",
        "a": [
          "keyDates"
        ]
      },
      {
        "q": "How does VAT return filing work in {name}?",
        "a": [
          "process"
        ]
      },
      {
        "q": "What does the VAT return in {name} contain?",
        "a": [
          "businessProcess"
        ]
      },
      {
        "q": "Where can I find the official rules for VAT returns in {name}?",
        "a": [
          "officialSources"
        ]
      }
    ]
  },
  "countries": [
    {
      "slug": "germany",
      "code": "DE",
      "name": "Germany",
      "shortName": "UStVA",
      "region": "Europe",
      "status": "mandatory",
      "statusLabel": "Required, monthly or quarterly",
      "reviewed": "October 2026",
      "title": "VAT Returns in Germany (UStVA): Rules Explained | Taxera",
      "description": "How VAT returns work in Germany: who files, the key dates, how the return is prepared and what your ERP has to deliver."
    },
    {
      "slug": "france",
      "code": "FR",
      "name": "France",
      "shortName": "CA3",
      "region": "Europe",
      "status": "mandatory",
      "statusLabel": "Required, regime changes in 2027",
      "reviewed": "October 2026",
      "title": "VAT Returns in France (CA3): Rules Explained | Taxera",
      "description": "How VAT returns work in France: who files, the key dates, how the return is prepared and what your ERP has to deliver."
    },
    {
      "slug": "italy",
      "code": "IT",
      "name": "Italy",
      "shortName": "LIPE",
      "region": "Europe",
      "status": "mandatory",
      "statusLabel": "Required, quarterly report",
      "reviewed": "October 2026",
      "title": "VAT Returns in Italy (LIPE): Rules Explained | Taxera",
      "description": "How VAT returns work in Italy: who files, the key dates, how the return is prepared and what your ERP has to deliver."
    },
    {
      "slug": "spain",
      "code": "ES",
      "name": "Spain",
      "shortName": "Modelo 303",
      "region": "Europe",
      "status": "mandatory",
      "statusLabel": "Required, quarterly or monthly",
      "reviewed": "October 2026",
      "title": "VAT Returns in Spain (Modelo 303): Rules Explained | Taxera",
      "description": "How VAT returns work in Spain: who files, the key dates, how the return is prepared and what your ERP has to deliver."
    },
    {
      "slug": "netherlands",
      "code": "NL",
      "name": "the Netherlands",
      "shortName": "Btw-aangifte",
      "region": "Europe",
      "status": "mandatory",
      "statusLabel": "Required, monthly, quarterly or annual",
      "reviewed": "October 2026",
      "title": "VAT Returns in the Netherlands: Rules Explained | Taxera",
      "description": "How VAT returns work in the Netherlands: who files, the key dates, how the return is prepared and what your ERP has to deliver."
    },
    {
      "slug": "poland",
      "code": "PL",
      "name": "Poland",
      "shortName": "JPK_V7",
      "region": "Europe",
      "status": "mandatory",
      "statusLabel": "Required, monthly or quarterly",
      "reviewed": "October 2026",
      "title": "VAT Returns in Poland (JPK_V7): Rules Explained | Taxera",
      "description": "How VAT returns work in Poland: who files, the key dates, how the return is prepared and what your ERP has to deliver."
    }
  ]
};
export default config;
